import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  ArrowLeft,
  BriefcaseBusiness,
  Check,
  ChevronLeft,
  ChevronRight,
  Circle,
  Download,
  Grid2X2,
  Image as ImageIcon,
  LayoutPanelLeft,
  MessageCircle,
  MonitorPlay,
  PanelLeftClose,
  PanelRightClose,
  Redo2,
  Save,
  SquareStack,
  Type,
  Undo2,
  Upload,
  Users,
  X,
} from 'lucide-react';
import type { AssetCategory } from '../types';
import { BrandMark } from './Brand';
import { AssetPanel } from './AssetPanel';
import { BoardView } from './BoardView';
import { CanvasEditor } from './CanvasEditor';
import { CellStrip } from './CellStrip';
import { Inspector } from './Inspector';
import { ExportDialog } from './ExportDialog';
import { RevisionsDialog } from './RevisionsDialog';
import { Presentation } from './Presentation';
import { useEditorStore } from '../store/editor';
import { loadProject, saveProject } from '../storage/db';
import { goHome, goToEditor } from '../utils/navigation';
import { AppHeader, type HeaderAction } from './AppHeader';
import { AdRail } from './AdRail';
import { createTemplateById } from '../model/templates';
import { exportProjectPackage, importProjectFile } from '../utils/export';

const categoryItems: Array<{ id: AssetCategory; label: string; icon: React.ReactNode }> = [
  { id: 'scenes', label: 'Scenes', icon: <ImageIcon size={20} /> },
  { id: 'characters', label: 'Characters', icon: <Users size={20} /> },
  { id: 'props', label: 'Props', icon: <BriefcaseBusiness size={20} /> },
  { id: 'text', label: 'Text', icon: <Type size={20} /> },
  { id: 'bubbles', label: 'Bubbles', icon: <MessageCircle size={20} /> },
  { id: 'shapes', label: 'Shapes', icon: <Circle size={20} /> },
  { id: 'upload', label: 'Upload', icon: <Upload size={20} /> },
];

export const Editor: React.FC<{ projectId: string }> = ({ projectId }) => {
  const project = useEditorStore((state) => state.project);
  const category = useEditorStore((state) => state.category);
  const viewMode = useEditorStore((state) => state.viewMode);
  const saveStatus = useEditorStore((state) => state.saveStatus);
  const past = useEditorStore((state) => state.past);
  const future = useEditorStore((state) => state.future);
  const sidebarOpen = useEditorStore((state) => state.sidebarOpen);
  const inspectorOpen = useEditorStore((state) => state.inspectorOpen);
  const initialize = useEditorStore((state) => state.initialize);
  const mutate = useEditorStore((state) => state.mutate);
  const undo = useEditorStore((state) => state.undo);
  const redo = useEditorStore((state) => state.redo);
  const setCategory = useEditorStore((state) => state.setCategory);
  const setViewMode = useEditorStore((state) => state.setViewMode);
  const setSaveStatus = useEditorStore((state) => state.setSaveStatus);
  const setExportOpen = useEditorStore((state) => state.setExportOpen);
  const setRevisionsOpen = useEditorStore((state) => state.setRevisionsOpen);
  const setPresentationOpen = useEditorStore((state) => state.setPresentationOpen);
  const setSidebarOpen = useEditorStore((state) => state.setSidebarOpen);
  const setInspectorOpen = useEditorStore((state) => state.setInspectorOpen);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const importRef = useRef<HTMLInputElement>(null);
  const [fileBusy, setFileBusy] = useState(false);
  const presentationOpen = useEditorStore((state) => state.presentationOpen);
  const [otherTabOpen, setOtherTabOpen] = useState(false);
  const saveTimer = useRef<number | null>(null);
  const tabId = useMemo(() => `${Date.now()}-${Math.random().toString(36).slice(2)}`, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError('');
    void loadProject(projectId).then((loadedProject) => {
      if (cancelled) return;
      if (!loadedProject) {
        setLoadError('This local project could not be found. It may have been deleted or stored in another browser profile.');
        setLoading(false);
        return;
      }
      initialize(loadedProject);
      document.title = `${loadedProject.title} — StoryboardSmith`;
      setLoading(false);
    }).catch((error) => {
      if (!cancelled) {
        setLoadError(error instanceof Error ? error.message : 'The project could not be loaded.');
        setLoading(false);
      }
    });
    return () => { cancelled = true; };
  }, [initialize, projectId]);

  useEffect(() => {
    if (!project || saveStatus !== 'unsaved') return undefined;
    if (saveTimer.current) window.clearTimeout(saveTimer.current);
    saveTimer.current = window.setTimeout(() => {
      setSaveStatus('saving');
      void saveProject(project).then(() => {
        // A completed older write must not mark subsequent edits as saved.
        if (useEditorStore.getState().project === project) setSaveStatus('saved');
      }).catch(() => {
        if (useEditorStore.getState().project === project) setSaveStatus('error');
      });
    }, 650);
    return () => {
      if (saveTimer.current) window.clearTimeout(saveTimer.current);
    };
  }, [project, saveStatus, setSaveStatus]);

  useEffect(() => {
    if (!project) return;
    document.title = `${project.title} — StoryboardSmith`;
  }, [project?.title]);

  useEffect(() => {
    const handler = (event: BeforeUnloadEvent) => {
      if (saveStatus === 'unsaved' || saveStatus === 'saving') {
        event.preventDefault();
        event.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [saveStatus]);

  useEffect(() => {
    if (typeof BroadcastChannel === 'undefined') return undefined;
    const channel = new BroadcastChannel(`storyboardsmith-${projectId}`);
    channel.onmessage = (event) => {
      const message = event.data as { type?: string; tabId?: string };
      if (message.tabId === tabId) return;
      if (message.type === 'open' || message.type === 'ping') {
        setOtherTabOpen(true);
        channel.postMessage({ type: 'pong', tabId });
      }
      if (message.type === 'pong') setOtherTabOpen(true);
      if (message.type === 'closed') setOtherTabOpen(false);
    };
    channel.postMessage({ type: 'open', tabId });
    const onClose = () => channel.postMessage({ type: 'closed', tabId });
    window.addEventListener('unload', onClose);
    return () => {
      channel.postMessage({ type: 'closed', tabId });
      channel.close();
      window.removeEventListener('unload', onClose);
    };
  }, [projectId, tabId]);

  if (loading) return <div className="app-loading"><div className="spinner" /><BrandMark /><p>Heating the forge…</p></div>;
  if (loadError || !project) return <div className="app-loading error-page"><BrandMark /><h1>Project unavailable</h1><p>{loadError}</p><button type="button" className="primary-button" onClick={goHome}>Return to dashboard</button></div>;

  // Save before in-app navigation; otherwise unmount would cancel the autosave timer.
  const persistCurrent = async () => {
    if (saveTimer.current) window.clearTimeout(saveTimer.current);
    const current = useEditorStore.getState().project;
    if (!current) return;
    setSaveStatus('saving');
    try {
      await saveProject(current);
      setSaveStatus(useEditorStore.getState().project === current ? 'saved' : 'unsaved');
    } catch (error) { setSaveStatus('error'); throw error; }
  };
  const runFileAction = async (action: () => Promise<void>) => {
    if (fileBusy) return;
    setFileBusy(true);
    try { await action(); }
    catch (error) { window.alert(error instanceof Error ? error.message : 'The file operation could not be completed.'); }
    finally { setFileBusy(false); }
  };
  const handleImport = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    void runFileAction(async () => {
      const imported = await importProjectFile(file);
      await persistCurrent();
      await saveProject(imported);
      goToEditor(imported.id);
    });
  };
  const fileActions: HeaderAction[] = [
    { label: 'New comic', icon: <Grid2X2 size={16} />, disabled: fileBusy, onSelect: () => void runFileAction(async () => {
      await persistCurrent();
      const next = createTemplateById('blank');
      await saveProject(next); goToEditor(next.id);
    }) },
    { label: 'Open comic…', icon: <Upload size={16} />, disabled: fileBusy, onSelect: () => importRef.current?.click() },
    { label: 'Save now', icon: <Save size={16} />, disabled: fileBusy, separatorBefore: true, onSelect: () => void runFileAction(persistCurrent) },
    { label: 'Download project backup', icon: <Download size={16} />, disabled: fileBusy, onSelect: () => void runFileAction(async () => { await persistCurrent(); await exportProjectPackage(useEditorStore.getState().project!); }) },
    { label: 'Export…', icon: <Download size={16} />, onSelect: () => setExportOpen(true) },
    { label: 'Revision history', icon: <SquareStack size={16} />, onSelect: () => setRevisionsOpen(true) },
    { label: 'Dashboard', icon: <ArrowLeft size={16} />, disabled: fileBusy, separatorBefore: true, onSelect: () => void runFileAction(async () => { await persistCurrent(); goHome(); }) },
  ];

  const saveLabel = saveStatus === 'saved' ? 'Saved locally' : saveStatus === 'saving' ? 'Saving…' : saveStatus === 'error' ? 'Save failed' : 'Unsaved changes';

  return (
    <div className={`editor-shell view-${viewMode} ${sidebarOpen ? '' : 'sidebar-collapsed'} ${inspectorOpen ? '' : 'inspector-collapsed'}`}>
      <AppHeader
        onHome={() => void runFileAction(async () => { await persistCurrent(); goHome(); })}
        fileActions={fileActions}
        leading={<div className="ss-comic-name">
          <input className="project-title-input" value={project.title} maxLength={200} aria-label="Project title" onChange={(event) => mutate((draft) => { draft.title = event.target.value || 'Untitled Storyboard'; })} />
          <span className={`save-indicator save-${saveStatus}`} role="status" title="Projects are stored in IndexedDB on this device.">{saveStatus === 'saved' ? <Check size={10} /> : <Save size={10} />}{saveLabel}</span>
        </div>}
        controls={<>
          <button type="button" className="icon-button" onClick={undo} disabled={past.length === 0} aria-label="Undo"><Undo2 size={17} /></button>
          <button type="button" className="icon-button" onClick={redo} disabled={future.length === 0} aria-label="Redo"><Redo2 size={17} /></button>
          <span className="header-divider" />
          <div className="view-switch" role="group" aria-label="Editor view">
            <button type="button" aria-pressed={viewMode === 'cell'} className={viewMode === 'cell' ? 'is-active' : ''} onClick={() => setViewMode('cell')}><LayoutPanelLeft size={15} /> Cell</button>
            <button type="button" aria-pressed={viewMode === 'board'} className={viewMode === 'board' ? 'is-active' : ''} onClick={() => setViewMode('board')}><Grid2X2 size={15} /> Board</button>
          </div>
          <button type="button" className="secondary-button present-button" onClick={() => setPresentationOpen(true)}><MonitorPlay size={16} /> Present</button>
          <button type="button" className="primary-button export-button" onClick={() => setExportOpen(true)}><Download size={16} /> Export</button>
          <button type="button" className="secondary-button" onClick={() => setRevisionsOpen(true)}><SquareStack size={16} /> Revision history</button>
        </>}
      />
      <input ref={importRef} hidden type="file" aria-label="Open comic file" accept=".smithboard,.json,application/zip,application/json" onChange={handleImport} />

      {otherTabOpen && (
        <div className="tab-warning" role="status"><strong>This project is open in another tab.</strong><span>Use one tab at a time to avoid replacing a newer local save.</span><button type="button" className="icon-button" onClick={() => setOtherTabOpen(false)} aria-label="Dismiss warning"><X size={17} /></button></div>
      )}

      <div className="ss-editor-body">
        <main className="ss-editor-main" aria-label="Comic workspace">
      {viewMode === 'cell' ? (
        <div className="editor-workspace">
          <nav className="category-rail" aria-label="Asset categories">
            {categoryItems.map((item) => (
              <button key={item.id} type="button" className={category === item.id && sidebarOpen ? 'is-active' : ''} onClick={() => { setCategory(item.id); setSidebarOpen(true); }}>
                {item.icon}<span>{item.label}</span>
              </button>
            ))}
          </nav>
          {sidebarOpen && <AssetPanel />}
          <section className="canvas-column">
            <button type="button" className="panel-collapse left-collapse" onClick={() => setSidebarOpen(!sidebarOpen)} aria-label={sidebarOpen ? 'Hide asset library' : 'Show asset library'}>{sidebarOpen ? <PanelLeftClose size={18} /> : <ChevronRight size={18} />}</button>
            <button type="button" className="panel-collapse right-collapse" onClick={() => setInspectorOpen(!inspectorOpen)} aria-label={inspectorOpen ? 'Hide inspector' : 'Show inspector'}>{inspectorOpen ? <PanelRightClose size={18} /> : <ChevronLeft size={18} />}</button>
            <CanvasEditor />
            <CellStrip />
          </section>
          {inspectorOpen && <Inspector />}
        </div>
      ) : <BoardView />}
        </main>
        <AdRail suspended={presentationOpen} />
      </div>

      <ExportDialog />
      <RevisionsDialog />
      <Presentation />
    </div>
  );
};
